import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, X, Bell } from "lucide-react";

/**
 * Persistent red banner shown at the top of every page when there are expiring contracts
 * or overdue/upcoming PVs. Dismisses only for the current session.
 */
export default function AlertBanner() {
  const [contracts, setContracts] = useState([]);
  const [pvs, setPvs] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("alerts_banner_dismissed") === "1";
    } catch { return false; }
  });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [c, p, ls] = await Promise.all([
          api.get("/alerts/expiring-contracts"),
          api.get("/alerts/missing-pvs"),
          api.get("/alerts/low-stock"),
        ]);
        if (!alive) return;
        setContracts(c.data.items || []);
        setPvs(p.data.items || []);
        setLowStock(ls.data.items || []);
      } catch {}
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const total = contracts.length + pvs.length + lowStock.length;
  if (total === 0 || dismissed) return null;

  const overdueContracts = contracts.filter((a) => a.zile_ramase < 0).length;
  const upcomingContracts = contracts.filter((a) => a.zile_ramase >= 0).length;
  const overduePvs = pvs.filter((a) => a.zile_ramase < 0).length;
  const upcomingPvs = pvs.filter((a) => a.zile_ramase >= 0).length;
  const lowStockCount = lowStock.length;

  return (
    <div
      className="relative border-b-2 border-red-600 shadow-lg animate-in slide-in-from-top duration-300"
      style={{ background: "linear-gradient(90deg, #dc2626 0%, #b91c1c 50%, #dc2626 100%)" }}
      data-testid="alert-banner"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <AlertTriangle className="w-6 h-6 text-white" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full animate-ping" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full" />
          </div>
          <p className="font-display font-black text-lg text-white tracking-tight">
            {total} ATENȚIONĂRI
          </p>
        </div>

        <div className="flex-1 min-w-[240px] flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/95">
          {overdueContracts > 0 && (
            <span className="font-semibold">
              🔴 {overdueContracts} contract{overdueContracts === 1 ? "" : "e"} EXPIRAT{overdueContracts === 1 ? "" : "E"}
            </span>
          )}
          {upcomingContracts > 0 && (
            <span className="font-semibold">
              🟠 {upcomingContracts} contract{upcomingContracts === 1 ? "" : "e"} expiră în &lt;30 zile
            </span>
          )}
          {overduePvs > 0 && (
            <span className="font-semibold">
              🔴 {overduePvs} PV{overduePvs === 1 ? "" : "-uri"} RESTANT{overduePvs === 1 ? "" : "E"}
            </span>
          )}
          {upcomingPvs > 0 && (
            <span className="font-semibold">
              🟡 {upcomingPvs} PV{upcomingPvs === 1 ? "" : "-uri"} de făcut curând
            </span>
          )}
          {lowStockCount > 0 && (
            <span className="font-semibold">
              📦 {lowStockCount} stoc{lowStockCount === 1 ? "" : "uri"} sub prag
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white text-red-700 px-4 py-1.5 rounded-md font-semibold text-sm hover:bg-yellow-100 transition-colors shadow"
            data-testid="alert-banner-view"
          >
            <Bell className="w-4 h-4" /> Vezi detalii
          </Link>
          <button
            onClick={() => {
              try { sessionStorage.setItem("alerts_banner_dismissed", "1"); } catch {}
              setDismissed(true);
            }}
            className="p-1.5 rounded-md text-white/80 hover:bg-white/10 transition-colors"
            aria-label="Închide"
            data-testid="alert-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
